import { useEffect, useMemo, useState } from "react";

import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { SearchableSelect } from "~/components/ui/searchable-select";
import { ALIACE_SPA_ID } from "~/constants/companies";
import { isApCreditNote } from "~/constants/siiDocTypes";
import { useAccounts } from "~/hooks/contabilidad/useAccounts";
import {
  useContabilizarSupplierInvoice,
  useSupplier,
  useSupplierCompanyProfiles,
} from "~/hooks/purchasing";
import { formatCurrency } from "~/utils/helpers";

import type { PostingLine } from "~/services/purchasing/supplierInvoiceService";
import type { SupplierInvoice } from "~/types/purchasing";

// IVA crédito fiscal account code. TODO(multiempresa): move to a per-company config; today only
// Aliace SPA has this code, so other empresas will surface "(falta cuenta)" until configured.
const IVA_CF_CODE = "1107001";

interface ContabilizarFacturaDialogProps {
  invoice: SupplierInvoice | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const clp = (n: number) => `$${formatCurrency(n)}`;
const digitsOnly = (s: string | null | undefined) =>
  (s ?? "").toUpperCase().replace(/[^0-9K]/g, "");

export function ContabilizarFacturaDialog({
  invoice,
  open,
  onOpenChange,
}: ContabilizarFacturaDialogProps) {
  // The empresa comes from the invoice (multiempresa AP); its chart drives the asiento.
  const companyId = invoice?.company_id ?? ALIACE_SPA_ID;
  const { data: supplier } = useSupplier(invoice?.supplier_id);
  const { data: profiles = [] } = useSupplierCompanyProfiles(invoice?.supplier_id);
  const profile = useMemo(
    () => profiles.find((p) => p.company_id === companyId),
    [profiles, companyId]
  );
  const { data: accounts = [] } = useAccounts({
    company_id: companyId,
    is_active: true,
  });
  const contabilizar = useContabilizarSupplierInvoice();
  const [gastoId, setGastoId] = useState("");
  const [glosa, setGlosa] = useState("");

  // Pre-fill the gasto with the proveedor's remembered default for this empresa (learn-as-you-go),
  // falling back to the legacy global default during the transition.
  useEffect(() => {
    setGastoId(
      profile?.default_expense_account_id ??
        supplier?.default_expense_account_id ??
        ""
    );
  }, [profile?.default_expense_account_id, supplier?.default_expense_account_id, invoice?.id]);

  // Pre-fill the glosa (ítems del DTE) — editable for the few facturas without XML / corrections.
  useEffect(() => {
    setGlosa(invoice?.glosa ?? "");
  }, [invoice?.glosa, invoice?.id]);

  // Imputación picker: any active account of the empresa (gasto, activo, pasivo, …) — e.g. a
  // financial-lease factura imputes the net to the leasing liability. Searchable by code/name.
  const gastoOptions = useMemo(
    () =>
      [...accounts]
        .sort((a, b) => a.code.localeCompare(b.code))
        .map((a) => ({
          value: a.id,
          label: `${a.code} · ${a.name}`,
          searchTerms: `${a.code} ${a.name}`,
        })),
    [accounts]
  );

  const acreedor = useMemo(() => {
    const code = digitsOnly(
      profile?.ap_account_code ?? supplier?.ap_account_code ?? invoice?.ap_account_code
    );
    return accounts.find((a) => a.code === code);
  }, [accounts, profile?.ap_account_code, supplier?.ap_account_code, invoice?.ap_account_code]);
  const ivaAccount = useMemo(
    () => accounts.find((a) => a.code === IVA_CF_CODE),
    [accounts]
  );
  const gastoAccount = accounts.find((a) => a.id === gastoId);

  const amounts = useMemo(() => {
    if (!invoice)
      return { base: 0, iva: 0, total: 0, isNC: false, otrosImpuestos: 0 };
    // Direction keyed on doc type, not sign: facturas (FE/FNE) store amounts negative and notas de
    // crédito (NC/NCEX) positive, so `total_clp < 0` would wrongly flip every entry. Amounts below
    // use abs().
    const isNC = isApCreditNote(invoice.doc_tipo_code);
    const statedNet =
      Math.abs(Number(invoice.neto)) +
      Math.abs(Number(invoice.exento)) +
      Math.abs(Number(invoice.impto_especifico));
    const iva = Math.abs(Number(invoice.iva));
    const total = Math.abs(Number(invoice.total_clp));
    // Taxes on the DTE beyond IVA that weren't captured into a column: either additional taxes that
    // ADD to the total (e.g. ESP. BEB. / impuesto adicional a las bebidas / ILA) or specific taxes
    // that net as a CREDIT and SUBTRACT from it (e.g. impuesto específico al petróleo diésel, código
    // SII 28, cuyo componente variable es recuperable). Contabilidad folds the whole remainder into
    // the net/expense account ("se deja igual en el neto", rebajándolo cuando el impuesto resta), so
    // we impute it to the gasto line and the asiento cuadra por construcción contra el total del DTE
    // (la cifra autoritativa, firmada por el SII).
    const otrosImpuestos = total - iva - statedNet; // signed: + adiciona, − rebaja al neto
    const base = statedNet + otrosImpuestos; // == total - iva
    return { base, iva, total, isNC, otrosImpuestos };
  }, [invoice]);

  // Build the preview rows. Normal factura: Dr gasto + Dr IVA CF | Cr proveedor.
  // Nota de crédito: the reverse.
  const rows = useMemo(() => {
    const { base, iva, total, isNC } = amounts;
    const r: {
      key: string;
      label: string;
      account_id?: string;
      debit: number;
      credit: number;
      editable?: boolean;
    }[] = [
      {
        key: "gasto",
        label: gastoAccount
          ? `${gastoAccount.code} · ${gastoAccount.name}`
          : "Cuenta de gasto…",
        account_id: gastoId || undefined,
        debit: isNC ? 0 : base,
        credit: isNC ? base : 0,
        editable: true,
      },
    ];
    if (iva > 0) {
      r.push({
        key: "iva",
        label: ivaAccount ? `${ivaAccount.code} · ${ivaAccount.name}` : "IVA CF",
        account_id: ivaAccount?.id,
        debit: isNC ? 0 : iva,
        credit: isNC ? iva : 0,
      });
    }
    r.push({
      key: "prov",
      label: acreedor ? `${acreedor.code} · ${acreedor.name}` : "Proveedores",
      account_id: acreedor?.id,
      debit: isNC ? total : 0,
      credit: isNC ? 0 : total,
    });
    return r;
  }, [amounts, gastoId, gastoAccount, ivaAccount, acreedor]);

  const totalDebit = rows.reduce((s, r) => s + r.debit, 0);
  const totalCredit = rows.reduce((s, r) => s + r.credit, 0);
  const cuadra = Math.abs(totalDebit - totalCredit) < 0.01;
  const missingAcct = rows.some((r) => !r.account_id);
  const canPost =
    !!invoice && !!gastoId && !!acreedor && cuadra && !missingAcct && !contabilizar.isPending;

  const handleConfirm = () => {
    if (!invoice || !canPost) return;
    const g = glosa.trim();
    const lines: PostingLine[] = rows.map((r) => ({
      account_id: r.account_id as string,
      description:
        r.key === "prov"
          ? `${invoice.razon_social ?? ""} · Factura ${invoice.folio}`.trim()
          : r.key === "iva"
            ? `IVA CF Factura ${invoice.folio}`
            : g
              ? `${g} · Factura ${invoice.folio}`
              : `Factura ${invoice.folio}`,
      debit: r.debit,
      credit: r.credit,
    }));
    const ref =
      `Factura ${invoice.folio}${g ? ` · ${g}` : ""} – ${supplier?.name ?? invoice.razon_social ?? ""}`.trim();
    contabilizar.mutate(
      {
        invoiceId: invoice.id,
        moveDate: invoice.emission_date,
        ref,
        lines,
        gastoAccountId: gastoId,
      },
      { onSuccess: () => onOpenChange(false) }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Contabilizar factura</DialogTitle>
          <DialogDescription>
            {invoice
              ? `${supplier?.name ?? invoice.razon_social ?? ""} · Factura ${invoice.folio} · ${clp(Math.abs(Number(invoice.total_clp)))}`
              : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Gasto (imputación) picker */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">
              Cuenta de imputación (del neto)
            </label>
            <SearchableSelect
              value={gastoId}
              onValueChange={setGastoId}
              options={gastoOptions}
              placeholder="Selecciona la cuenta…"
              emptyMessage="Sin cuentas"
            />
            <p className="text-xs text-muted-foreground">
              Se recordará como la cuenta por defecto de este proveedor.
            </p>
            {Math.abs(amounts.otrosImpuestos) > 0.5 && (
              <p className="text-xs text-muted-foreground">
                {amounts.otrosImpuestos > 0
                  ? `Incluye ${clp(amounts.otrosImpuestos)} de otros impuestos de la factura (p. ej. ESP. BEB. / impuesto adicional) imputados al neto.`
                  : `Rebaja ${clp(Math.abs(amounts.otrosImpuestos))} de impuesto específico de la factura (p. ej. impuesto al diésel) imputado al neto.`}
              </p>
            )}
          </div>

          {/* Glosa (descripción del asiento) */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">
              Glosa (descripción)
            </label>
            <Input
              value={glosa}
              onChange={(e) => setGlosa(e.target.value)}
              placeholder="Descripción del ítem…"
            />
            <p className="text-xs text-muted-foreground">
              Se toma de los ítems de la factura; puedes editarla.
            </p>
          </div>

          {/* Asiento preview */}
          <div className="overflow-hidden rounded-lg border border-gray-300">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-100 text-gray-600">
                  <th className="px-3 py-2 text-left font-medium">Cuenta</th>
                  <th className="px-3 py-2 text-right font-medium">Debe</th>
                  <th className="px-3 py-2 text-right font-medium">Haber</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key} className="border-b border-gray-100 last:border-0">
                    <td className="px-3 py-2 text-gray-800">
                      {r.label}
                      {!r.account_id && (
                        <span className="ml-2 text-xs text-red-600">(falta cuenta)</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {r.debit ? clp(r.debit) : "-"}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {r.credit ? clp(r.credit) : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-gray-200 bg-gray-50 font-medium">
                  <td className="px-3 py-2 text-right text-gray-600">Totales</td>
                  <td className="px-3 py-2 text-right font-mono">{clp(totalDebit)}</td>
                  <td className="px-3 py-2 text-right font-mono">{clp(totalCredit)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {cuadra ? (
            <div className="flex items-center gap-2 text-sm text-green-700">
              <CheckCircle2 className="h-4 w-4" /> El asiento cuadra
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-red-700">
              <AlertTriangle className="h-4 w-4" /> Descuadrado por{" "}
              {clp(Math.abs(totalDebit - totalCredit))}
            </div>
          )}

          <Badge variant="secondary" className="bg-gray-100 text-gray-600">
            Diario PROV · Borrador (lo publica Contabilidad en Movimientos)
          </Badge>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleConfirm} disabled={!canPost}>
            {contabilizar.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Guardar borrador
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
